import { Box, Modal, Theme } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import { Container } from "@outschool/ui-legacy-component-library";
import React, { SetStateAction } from "react";

import ActivityDetailDescription from "../ActivityDetailDescription";
import ClassDetailsNav from "../ClassDetailsNav";
import ClubExplanationSection from "../ClubExplanationSection";
import DesktopActivityCard from "../DesktopActivityCard";
import DesktopActivityHeader from "../DesktopActivityHeader";
import Disclaimer from "../Disclaimer";
import LearningGoals from "../LearningGoals";
import OtherDetails from "../OtherDetails";
import SyllabusSummary from "../SyllabusSummary";
import WhatsIncluded from "../WhatsIncluded";
import {
  DesktopClassDetailsMainBodyProps,
  gradientFadeStyle,
} from "./Components";
import { ReviewsSection } from "./Reviews";

export default function DesktopClassDetailsMainBody({
  // props
  activity,
  activityWithEnrollableTimes,
  canEnroll,
  classNavTabs,
  facebookAppId,
  hasOneOnOneAvailabilities,
  isActivitySavedByCurrentUser,
  isAvailabilityModalOpen,
  isCrossSellVariant,
  isLoadingOneOnOneAvailabilities,
  isOrganizationEnrollment,
  leader,
  onClickMoreLikeThis,
  onClickViewReviews,
  onClickViewTeacher,
  setIsAvailabilityModalOpen,
  setTabValue,
  showAvailableTimes,
  tabValue,
  // refs
  refsContainer,
  sectionsRef,
  viewAvailabilityRef,
  // memos & elements
  ActivityEnrollableTimes,
  AskAQuestionButton,
  AvailableTimes,
  ChooseATimeOrClassScheduleRequestButton,
  MessageModal,
  ReviewRespondButton,
  SectionCallToAction,
  TeacherSection,
  TutorEnrollButton,
  MemberPlanDiscountTooltip,
}: DesktopClassDetailsMainBodyProps) {
  ActivityEnrollableTimes = ActivityEnrollableTimes ?? <></>;
  AvailableTimes = AvailableTimes ?? <></>;

  const AvailableTimesView = (
    <AvailableTimesViewComponent
      activity={activity}
      hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
      isAvailabilityModalOpen={isAvailabilityModalOpen}
      isCrossSellVariant={isCrossSellVariant}
      isLoadingOneOnOneAvailabilities={isLoadingOneOnOneAvailabilities}
      sectionsRef={sectionsRef}
      setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
      showAvailableTimes={showAvailableTimes}
      ActivityEnrollableTimes={ActivityEnrollableTimes}
      AvailableTimes={AvailableTimes}
    />
  );

  return (
    <Container
      sx={{
        display: "flex",
        marginY: "40px",
        alignItems: "flex-start",
      }}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          marginRight: "3em",
          maxWidth: "76rem",
          minWidth: "0", // override automatic minimum width
          flexDirection: "column",
          [theme.breakpoints.down("lg")]: {
            marginRight: "1.5em",
          },
        })}
        ref={refsContainer.root}
      >
        <Box ref={refsContainer.about.header}>
          <DesktopActivityHeader
            activity={activity}
            leader={leader}
            facebookAppId={facebookAppId}
            isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
            onClickViewTeacher={onClickViewTeacher}
            MessageTeacherButton={AskAQuestionButton}
            onClickViewReviews={onClickViewReviews}
          />
        </Box>
        <ClassDetailsNav tabs={classNavTabs ?? []} />
        <Box
          flex
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "3.2rem",
            gap: "9.6rem",
          }}
        >
          <WhatsIncluded
            activity={activity}
            headerRef={refsContainer.about.whatsIncluded}
          />
          <ActivityDetailDescription
            activity={activity}
            MessageModal={MessageModal}
            isCrossSell={isCrossSellVariant}
            headerRef={refsContainer.about.classExperience}
            gradientStyles={gradientFadeStyle}
          />
          <LearningGoals
            learningGoals={activity.learningGoals}
            headerRef={refsContainer.learningOverview.learningGoals}
          />
          <SyllabusSummary
            activity={activity}
            headerRef={refsContainer.learningOverview.syllabus}
          />
          <OtherDetails
            activity={activity}
            headerRef={refsContainer.learningOverview.otherDetails}
          />
          {activity.disclaimer && <Disclaimer text={activity.disclaimer} />}
          {AvailableTimesView}
          {!!hasOneOnOneAvailabilities &&
            canEnroll &&
            TutorEnrollButton === undefined && (
              <Box ref={viewAvailabilityRef} sx={{ paddingY: 96 }}>
                {ActivityEnrollableTimes}
              </Box>
            )}
          {activity.isClub && (
            <ClubExplanationSection
              activity={activity}
              isOrganizationEnrollment={isOrganizationEnrollment}
              SectionCallToAction={SectionCallToAction}
            />
          )}
          {TeacherSection}
          {!!tabValue && !!setTabValue && (
            <ReviewsSection
              activity={activity}
              reviewsRef={refsContainer.reviews}
              setTabValue={setTabValue}
              tabValue={tabValue}
              ReviewRespondButton={ReviewRespondButton}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ position: "sticky", top: "40px" }}>
        <DesktopActivityCard
          activity={activity}
          activityWithEnrollableTimes={activityWithEnrollableTimes}
          leader={leader}
          isOrganizationEnrollment={isOrganizationEnrollment}
          isLoadingOneOnOneAvailabilities={isLoadingOneOnOneAvailabilities}
          hasOneOnOneAvailabilities={hasOneOnOneAvailabilities}
          onClickViewReviews={onClickViewReviews}
          onClickMoreLikeThis={onClickMoreLikeThis}
          sectionsRef={sectionsRef}
          viewAvailabilityRef={viewAvailabilityRef}
          SectionCallToAction={SectionCallToAction}
          ChooseATimeOrClassScheduleRequestButton={
            ChooseATimeOrClassScheduleRequestButton
          }
          isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
          facebookAppId={facebookAppId}
          setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
          TutorEnrollButton={TutorEnrollButton}
          MessageModal={MessageModal}
          MemberPlanDiscountTooltip={MemberPlanDiscountTooltip}
        />
      </Box>
    </Container>
  );
}

function AvailableTimesViewComponent({
  activity,
  hasOneOnOneAvailabilities,
  isAvailabilityModalOpen,
  isCrossSellVariant,
  isLoadingOneOnOneAvailabilities,
  sectionsRef,
  setIsAvailabilityModalOpen,
  showAvailableTimes,
  ActivityEnrollableTimes,
  AvailableTimes,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  hasOneOnOneAvailabilities: boolean;
  isAvailabilityModalOpen?: boolean;
  isCrossSellVariant?: boolean;
  isLoadingOneOnOneAvailabilities: boolean;
  sectionsRef: React.RefObject<HTMLElement>;
  showAvailableTimes: boolean;
  setIsAvailabilityModalOpen?: React.Dispatch<SetStateAction<boolean>>;
  ActivityEnrollableTimes?: React.JSX.Element | null;
  AvailableTimes?: React.JSX.Element | null;
}) {
  ActivityEnrollableTimes = ActivityEnrollableTimes ?? <></>;
  AvailableTimes = AvailableTimes ?? <></>;
  if (isCrossSellVariant) {
    return (
      <Modal
        open={!!isAvailabilityModalOpen}
        onClose={() =>
          !!setIsAvailabilityModalOpen && setIsAvailabilityModalOpen(false)
        }
        sx={{ padding: "2.5em", maxWidth: 824, maxHeight: 758 }}
      >
        {hasOneOnOneAvailabilities ||
        (activity.hasTeacherSchedule && isLoadingOneOnOneAvailabilities)
          ? ActivityEnrollableTimes
          : AvailableTimes}
      </Modal>
    );
  } else if (showAvailableTimes) {
    return <Box ref={sectionsRef}>{AvailableTimes}</Box>;
  }

  return <></>;
}
